import api from '@pray/shared/utils/api';

import type { WatchAnalyticsApiResponse, WatchAnalyticsData } from './types';

interface GetWatchAnalyticsParams {
  artistId: string;
  startDate?: string;
  endDate?: string;
  // Optional per-program filter ("Stand on the Word" / "Washington Watch"). Absent = combined.
  seriesTitle?: string;
  bustCache?: boolean;
}

/**
 * Maps API response to client-friendly format
 */
function mapWatchAnalytics(data: WatchAnalyticsApiResponse): WatchAnalyticsData {
  const watchesByDate = data.watches_by_date ?? { historical_total: 0, daily_data: [] };

  return {
    artistName: data.artist_name,
    totalWatches: data.total_watches ?? 0,
    totalUniqueWatchers: data.total_unique_watchers ?? 0,
    totalWatchDuration: data.total_watch_duration ?? 0,
    totalCompletedWatches: data.total_completed_watches ?? 0,
    watchesByDate: {
      historicalTotal: watchesByDate.historical_total ?? 0,
      dailyData: watchesByDate.daily_data ?? [],
    },
    topContent: (data.top_content ?? []).map((item) => ({
      rank: item.rank,
      contentTitle: item.content_title,
      seriesTitle: item.series_title,
      totalCount: item.total_count,
      totalDuration: item.total_duration,
      totalCompleted: item.total_completed,
    })),
    watchByRegion: data.watch_by_region ?? {},
    dateRange: {
      startDate: data.date_range?.start_date ?? '',
      endDate: data.date_range?.end_date ?? '',
    },
    programs: (data.programs ?? []).map((p) => ({ name: p.name, count: p.count })),
    seriesTitle: data.series_title ?? null,
  };
}

/**
 * Get watch analytics for an artist
 */
export async function getWatchAnalytics({
  artistId,
  startDate,
  endDate,
  seriesTitle,
  bustCache,
}: GetWatchAnalyticsParams): Promise<WatchAnalyticsData> {
  if (!artistId) throw new Error('Artist ID is required');

  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (seriesTitle) params.append('seriesTitle', seriesTitle);
  if (bustCache) params.append('bustCache', 'true');

  const queryString = params.toString();
  const url = `/web/studio/artists/${artistId}/analytics/watch${queryString ? `?${queryString}` : ''}`;

  const response = await api.executeGetRequest<{ data: WatchAnalyticsApiResponse }>(url);

  return mapWatchAnalytics(response.data.data);
}
